import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  HiOutlineMail, 
  HiOutlinePhone, 
  HiOutlineLocationMarker 
} from 'react-icons/hi';
import { 
  FaFacebookF, 
  FaInstagram, 
  FaPinterestP, 
  FaTwitter 
} from 'react-icons/fa';

const Footer = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-luxury-charcoal text-white">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <h2 className="font-serif text-3xl font-semibold tracking-wider text-luxury-gold mb-6">
              LUXE
            </h2>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">
              {t('footer.aboutUsDesc')}
            </p>
            <div className="flex space-x-4">
              <a href="#" className="w-10 h-10 border border-gray-700 flex items-center justify-center hover:border-luxury-gold hover:text-luxury-gold transition-colors">
                <FaFacebookF className="w-4 h-4" />
              </a>
              <a href="#" className="w-10 h-10 border border-gray-700 flex items-center justify-center hover:border-luxury-gold hover:text-luxury-gold transition-colors">
                <FaInstagram className="w-4 h-4" />
              </a>
              <a href="#" className="w-10 h-10 border border-gray-700 flex items-center justify-center hover:border-luxury-gold hover:text-luxury-gold transition-colors">
                <FaPinterestP className="w-4 h-4" />
              </a>
              <a href="#" className="w-10 h-10 border border-gray-700 flex items-center justify-center hover:border-luxury-gold hover:text-luxury-gold transition-colors">
                <FaTwitter className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-sm uppercase tracking-widest mb-6">{t('footer.shop')}</h3>
            <ul className="space-y-3">
              <li>
                <Link to="/shop?category=skincare" className="text-gray-400 hover:text-luxury-gold transition-colors text-sm">
                  {t('footer.skincare')}
                </Link>
              </li>
              <li>
                <Link to="/shop?category=makeup" className="text-gray-400 hover:text-luxury-gold transition-colors text-sm">
                  {t('footer.makeup')}
                </Link>
              </li>
              <li>
                <Link to="/shop?category=perfumes" className="text-gray-400 hover:text-luxury-gold transition-colors text-sm">
                  {t('footer.perfumes')}
                </Link>
              </li>
              <li>
                <Link to="/shop?category=jewelry" className="text-gray-400 hover:text-luxury-gold transition-colors text-sm">
                  {t('footer.jewelry')}
                </Link>
              </li>
              <li>
                <Link to="/shop?category=clothes" className="text-gray-400 hover:text-luxury-gold transition-colors text-sm">
                  {t('footer.clothes')}
                </Link>
              </li>
              <li>
                <Link to="/shop?newArrival=true" className="text-gray-400 hover:text-luxury-gold transition-colors text-sm">
                  {t('footer.newArrivals')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Help */}
          <div>
            <h3 className="text-sm uppercase tracking-widest mb-6">{t('footer.help')}</h3>
            <ul className="space-y-3">
              <li>
                <a href="#" className="text-gray-400 hover:text-luxury-gold transition-colors text-sm">
                  {t('footer.shipping')}
                </a>
              </li>
              <li>
                <a href="#" className="text-gray-400 hover:text-luxury-gold transition-colors text-sm">
                  {t('footer.trackOrder')}
                </a>
              </li>
              <li>
                <a href="#" className="text-gray-400 hover:text-luxury-gold transition-colors text-sm">
                  {t('footer.faq')}
                </a>
              </li>
              <li>
                <a href="#" className="text-gray-400 hover:text-luxury-gold transition-colors text-sm">
                  {t('footer.sizeGuide')}
                </a>
              </li>
              <li>
                <a href="#" className="text-gray-400 hover:text-luxury-gold transition-colors text-sm">
                  {t('footer.contactUs')}
                </a>
              </li>
              <li>
                <a href="#" className="text-gray-400 hover:text-luxury-gold transition-colors text-sm">
                  {t('footer.privacyPolicy')}
                </a>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-sm uppercase tracking-widest mb-6">{t('footer.contact')}</h3>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <HiOutlineLocationMarker className="w-5 h-5 text-luxury-gold flex-shrink-0 mt-0.5" />
                <span className="text-gray-400 text-sm">
                  {t('footer.address')}
                </span>
              </li>
              <li className="flex items-center space-x-3">
                <HiOutlinePhone className="w-5 h-5 text-luxury-gold flex-shrink-0" />
                <a href="tel:+1234567890" className="text-gray-400 hover:text-luxury-gold transition-colors text-sm">
                  +1 (234) 567-890
                </a>
              </li>
              <li className="flex items-center space-x-3">
                <HiOutlineMail className="w-5 h-5 text-luxury-gold flex-shrink-0" />
                <a href="mailto:hello@luxestore.com" className="text-gray-400 hover:text-luxury-gold transition-colors text-sm">
                  hello@luxestore.com
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between space-y-4 md:space-y-0">
            <p className="text-gray-500 text-sm">
              © {currentYear} LUXE. {t('footer.allRightsReserved')}
            </p>
            <div className="flex items-center space-x-6">
              <img src="https://upload.wikimedia.org/wikipedia/commons/0/04/Visa.svg" alt="Visa" className="h-6 opacity-50" />
              <img src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" alt="Mastercard" className="h-6 opacity-50" />
              <img src="https://upload.wikimedia.org/wikipedia/commons/b/b5/PayPal.svg" alt="PayPal" className="h-6 opacity-50" />
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
